import { z } from 'zod';

import { isDefined } from '../../../utils.js';
import {
  BaseFlowBlockSchema,
  BlockType,
  ConsumerOfPatientSchema,
  ConsumerOfProviderSchema,
  LookbackDurationSchema,
  LookbackDurationUnit,
  ProducedType,
  returnTypeSchema,
} from '../base/baseSchema.js';

export enum FhirEndpoint {
  GetPatient = 'GetPatient',
  PractitionerSearch = 'PractitionerSearch',
  ListEncounters = 'ListEncounters',
  BookedAppointments = 'BookedAppointments',
  LabValues = 'LabValues',
  ServiceRequests = 'ServiceRequests',
  Medications = 'Medications',
  Conditions = 'Conditions',
  Documents = 'Documents',
  Binaries = 'Binaries',
  SurgicalHistories = 'SurgicalHistories',
  Coverages = 'Coverages',
}

const BaseFhirBlockConfigSchema = z.object({
  endpoint: z.nativeEnum(FhirEndpoint),
  patient: ConsumerOfPatientSchema({ isRequired: true }),
});

const GetPatientConfigSchema = BaseFhirBlockConfigSchema.omit({ patient: true }).extend({
  endpoint: z.literal(FhirEndpoint.GetPatient),
  patient: ConsumerOfPatientSchema({ isRequired: false }).optional(),
  patientSearchFilters: z
    .object({
      lastName: z.string().min(1, 'Last name is required'),
      dateOfBirth: z.date().or(z.string().min(1, 'Date of birth is required')),
      phoneNumber: z.string().optional(),
      ehrId: z.string().optional(),
      firstName: z.string().min(1, 'First name is required'),
      email: z.string().optional(),
      patientId: z.string().optional(),
      zipCode: z.string().optional(),
      patientEhrIdToMatch: z.string().optional(),
    })
    .optional()
    .default({
      lastName: '',
      dateOfBirth: '',
      firstName: '',
    }),
  returnType: returnTypeSchema(ProducedType.FhirPatientResponse),
});
export type GetPatientConfig = z.infer<typeof GetPatientConfigSchema>;

const PractitionerSearchConfigSchema = BaseFhirBlockConfigSchema.omit({ patient: true }).extend({
  endpoint: z.literal(FhirEndpoint.PractitionerSearch),
  practitionerFilters: z
    .object(
      {
        firstName: z.string().optional(),
        lastName: z.string().optional(),
        npi: z.string().optional(),
        provider: ConsumerOfProviderSchema.optional(),
      },
      { message: 'Practitioner information is required' }
    )
    .optional()
    .default({})
    .superRefine((data, context) => {
      if (!isDefined(data.provider) && !isDefined(data.npi)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Either provider or npi must be set for practitioner search',
          path: ['npi'],
        });
      }
    }),
  returnType: returnTypeSchema(ProducedType.Practitioner),
});

const ListEncountersConfigSchema = BaseFhirBlockConfigSchema.extend({
  endpoint: z.literal(FhirEndpoint.ListEncounters),
  returnType: returnTypeSchema(ProducedType.Encounters),
});

const BookedAppointmentsConfigSchema = BaseFhirBlockConfigSchema.extend({
  endpoint: z.literal(FhirEndpoint.BookedAppointments),
  appointmentFilters: z
    .object({
      after: LookbackDurationSchema,
    })
    .optional()
    .default({
      after: {
        value: -1,
        unit: LookbackDurationUnit.Days,
      },
    }),
  returnType: returnTypeSchema(ProducedType.Appointments),
});

const LabValuesConfigSchema = BaseFhirBlockConfigSchema.extend({
  endpoint: z.literal(FhirEndpoint.LabValues),
  labValueFilters: z
    .object({
      categories: z.array(z.string()).optional(),
    })
    .optional(),
  returnType: returnTypeSchema(ProducedType.LabValues),
});

const ServiceRequestsConfigSchema = BaseFhirBlockConfigSchema.extend({
  endpoint: z.literal(FhirEndpoint.ServiceRequests),
  serviceRequestFilters: z
    .object({
      providerNpi: z.string().optional(),
      providerId: z.string().optional(),
      encounterId: z.string().optional(),
    })
    .optional(),
  returnType: returnTypeSchema(ProducedType.ServiceRequests),
});

const MedicationsConfigSchema = BaseFhirBlockConfigSchema.extend({
  endpoint: z.literal(FhirEndpoint.Medications),
  returnType: returnTypeSchema(ProducedType.Medications),
});

const ConditionsConfigSchema = BaseFhirBlockConfigSchema.extend({
  endpoint: z.literal(FhirEndpoint.Conditions),
  conditionFilters: z
    .object({
      verificationStatuses: z.array(z.string()).optional(),
    })
    .optional(),
  returnType: returnTypeSchema(ProducedType.Conditions),
});

const CoveragesConfigSchema = BaseFhirBlockConfigSchema.extend({
  endpoint: z.literal(FhirEndpoint.Coverages),
  returnType: returnTypeSchema(ProducedType.Coverages),
});

const DocumentsConfigSchema = BaseFhirBlockConfigSchema.extend({
  endpoint: z.literal(FhirEndpoint.Documents),
  returnType: returnTypeSchema(ProducedType.DocumentMetadata),
});
export type DocumentsConfig = z.infer<typeof DocumentsConfigSchema>;

const BinariesConfigSchema = BaseFhirBlockConfigSchema.extend({
  endpoint: z.literal(FhirEndpoint.Binaries),
  binaryFilters: z
    .object({
      bucketName: z
        .string({ message: 'Bucket name is required' })
        .min(1, 'Bucket name is required'),
      pathPrefix: z
        .string({ message: 'Path prefix is required' })
        .min(1, 'Path prefix is required'),
      binaryUrls: z.array(z.string().min(1, { message: 'Binary URL is required' }), {
        message: 'Binary URLs are required',
      }),
    })
    .optional()
    .default({
      bucketName: '',
      pathPrefix: '',
      binaryUrls: [],
    }),
  returnType: returnTypeSchema(ProducedType.DocumentBinaries),
});
export type BinariesConfig = z.infer<typeof BinariesConfigSchema>;

const SurgicalHistoriesConfigSchema = BaseFhirBlockConfigSchema.extend({
  endpoint: z.literal(FhirEndpoint.SurgicalHistories),
  returnType: returnTypeSchema(ProducedType.SurgicalHistories),
});

export const FhirConfigSchema = z.discriminatedUnion('endpoint', [
  GetPatientConfigSchema,
  PractitionerSearchConfigSchema,
  ListEncountersConfigSchema,
  BookedAppointmentsConfigSchema,
  LabValuesConfigSchema,
  ServiceRequestsConfigSchema,
  MedicationsConfigSchema,
  ConditionsConfigSchema,
  DocumentsConfigSchema,
  BinariesConfigSchema,
  SurgicalHistoriesConfigSchema,
  CoveragesConfigSchema,
]);
export type FhirConfig = z.infer<typeof FhirConfigSchema>;

export const FhirBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.Fhir),
  config: FhirConfigSchema,
});

export type FhirBlock = z.infer<typeof FhirBlockSchema>;
